import { c as _c } from "react/compiler-runtime";
import { batchDispatch } from '@/shared/utils/batchDispatch';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { differenceInDays, differenceInHours, format, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { archiveNegociacaoCaso, exportNegociacaoCasosCsv, fetchNegociacaoCasos, pauseNegociacaoCaso, resumeNegociacaoCaso, selectNegociacaoCasos, selectNegociacaoCasosError, selectNegociacaoCasosLoading, selectNegociacaoCasosPagination, selectNegociacaoExportingCsv } from '../store';
import type { BulkActionResult, ConversionTimelineData, LoteChartData, LoteDashboardMetrics, LoteFilters, LoteMetrics, LoteSectionData, LoteTemperatureSection, QuickViewData, TemperatureDistributionData, TimeMetricsByTemperature } from '../types/lote.types';
import type { NegociacaoListRow } from '../types/negociacoesUiTypes';
import { toNegociacaoListRow } from '../utils';
const defaultFilters: LoteFilters = {
  search: '',
  type: [],
  status: []
};
const FETCH_LIMIT = 500;
const CHART_COLORS = {
  hot: 'rgb(220, 38, 38)',
  warm: 'rgb(217, 119, 6)',
  cold: 'rgb(30, 136, 229)',
  success: 'rgb(22, 163, 74)',
  neutral: 'rgb(115, 115, 115)'
};
interface UseNegociacoesLoteReturn {
  sections: LoteSectionData[];
  metrics: LoteMetrics;
  dashboardMetrics: LoteDashboardMetrics;
  chartData: LoteChartData;
  filters: LoteFilters;
  selectedIds: Set<string>;
  isLoading: boolean;
  error: string | null;
  quickViewData: QuickViewData | null;
  isQuickViewOpen: boolean;
  expandedSections: Set<LoteTemperatureSection>;
  isExportingCsv: boolean;
  onSearchChange: (value: string) => void;
  onTypeToggle: (value: string) => void;
  onStatusToggle: (value: string) => void;
  onClearFilters: () => void;
  applyFilters: (newFilters: LoteFilters) => void;
  toggleSelect: (id: string) => void;
  toggleSelectAll: (sectionId: LoteTemperatureSection) => void;
  clearSelection: () => void;
  openQuickView: (item: NegociacaoListRow) => void;
  closeQuickView: () => void;
  pauseCaso: (caseId: string) => Promise<void>;
  resumeCaso: (caseId: string) => Promise<void>;
  archiveCaso: (caseId: string) => Promise<void>;
  pauseSelected: () => Promise<BulkActionResult>;
  resumeSelected: () => Promise<BulkActionResult>;
  archiveSelected: () => Promise<BulkActionResult>;
  exportSelected: (format: 'csv' | 'xlsx') => Promise<void>;
  toggleSectionExpand: (sectionId: LoteTemperatureSection) => void;
  scrollToSection: (sectionId: LoteTemperatureSection) => void;
  refetch: () => void;
}
export const useNegociacoesLote = () => {
  const $ = _c(174);
  const dispatch = useAppDispatch();
  const casos = useAppSelector(selectNegociacaoCasos);
  const isLoading = useAppSelector(selectNegociacaoCasosLoading);
  const error = useAppSelector(selectNegociacaoCasosError);
  useAppSelector(selectNegociacaoCasosPagination);
  const isExportingCsv = useAppSelector(selectNegociacaoExportingCsv);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchQuery, setSearchQuery] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [selectedIds, setSelectedIds] = useState(t0);
  const [quickViewData, setQuickViewData] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set(["INTERVENTION", "HOT", "WARM", "COLD"]);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [expandedSections, setExpandedSections] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== filters.search) {
    t2 = () => {
      const handle = setTimeout(() => {
        setSearchQuery(filters.search.trim());
      }, 350);
      return () => clearTimeout(handle);
    };
    t3 = [filters.search];
    $[2] = filters.search;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== casos) {
    t4 = casos.map(toNegociacaoListRow);
    $[5] = casos;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const allRows = t4;
  let result;
  if ($[7] !== allRows || $[8] !== filters.status || $[9] !== filters.type || $[10] !== searchQuery) {
    result = [...allRows];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(n => n.client.toLowerCase().includes(query) || n.negotiationName.toLowerCase().includes(query));
    }
    if (filters.type.length > 0) {
      result = result.filter(n_0 => filters.type.includes(n_0.type));
    }
    if (filters.status.length > 0) {
      result = result.filter(n_1 => filters.status.includes(n_1.status));
    }
    $[7] = allRows;
    $[8] = filters.status;
    $[9] = filters.type;
    $[10] = searchQuery;
    $[11] = result;
  } else {
    result = $[11];
  }
  const filteredRows = result;
  let t5;
  if ($[12] !== filteredRows) {
    t5 = filteredRows.filter(_temp);
    $[12] = filteredRows;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const interventionItems = t5;
  let t6;
  if ($[14] !== filteredRows) {
    t6 = filteredRows.filter(_temp2);
    $[14] = filteredRows;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const hotItems = t6;
  let t7;
  if ($[16] !== filteredRows) {
    t7 = filteredRows.filter(_temp3);
    $[16] = filteredRows;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const warmItems = t7;
  let t8;
  if ($[18] !== filteredRows) {
    t8 = filteredRows.filter(_temp4);
    $[18] = filteredRows;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const coldItems = t8;
  let t9;
  if ($[20] !== interventionItems) {
    t9 = {
      id: "INTERVENTION",
      title: "Interven\xE7\xE3o Humana Necess\xE1ria",
      items: interventionItems,
      count: interventionItems.length,
      colorClass: "text-warning-600",
      bgClass: "bg-warning-50",
      borderClass: "border-warning-200",
      iconType: "alert"
    };
    $[20] = interventionItems;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== hotItems) {
    t10 = {
      id: "HOT",
      title: "Temperatura Quente",
      items: hotItems,
      count: hotItems.length,
      colorClass: "text-error-600",
      bgClass: "bg-error-50",
      borderClass: "border-error-200",
      iconType: "flame"
    };
    $[22] = hotItems;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== warmItems) {
    t11 = {
      id: "WARM",
      title: "Temperatura Morna",
      items: warmItems,
      count: warmItems.length,
      colorClass: "text-warning-600",
      bgClass: "bg-warning-50",
      borderClass: "border-warning-200",
      iconType: "thermometer"
    };
    $[24] = warmItems;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== coldItems) {
    t12 = {
      id: "COLD",
      title: "Temperatura Fria",
      items: coldItems,
      count: coldItems.length,
      colorClass: "text-information-600",
      bgClass: "bg-information-50",
      borderClass: "border-information-200",
      iconType: "snowflake"
    };
    $[26] = coldItems;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t9) {
    t13 = [t9, t10, t11, t12];
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const sections = t13;
  let t14;
  if ($[33] !== sections) {
    t14 = sections.find(_temp5)?.count || 0;
    $[33] = sections;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== sections) {
    t15 = sections.find(_temp6)?.count || 0;
    $[35] = sections;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== sections) {
    t16 = sections.find(_temp7)?.count || 0;
    $[37] = sections;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== sections) {
    t17 = sections.find(_temp8)?.count || 0;
    $[39] = sections;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== filteredRows.length || $[42] !== t14 || $[43] !== t15 || $[44] !== t16 || $[45] !== t17) {
    t18 = {
      total: filteredRows.length,
      intervention: t14,
      hot: t15,
      warm: t16,
      cold: t17
    };
    $[41] = filteredRows.length;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const metrics = t18;
  let agreementsClosed;
  let avgDaysOpen;
  let casosComInteracao;
  let conversionRate;
  let noAgreement;
  let t19;
  let totalValue;
  if ($[47] !== casos || $[48] !== filteredRows) {
    const now = new Date();
    let t20;
    if ($[56] !== filteredRows) {
      t20 = new Set(filteredRows.map(_temp9));
      $[56] = filteredRows;
      $[57] = t20;
    } else {
      t20 = $[57];
    }
    const filteredIds = t20;
    let t21;
    if ($[58] !== filteredIds) {
      t21 = c => filteredIds.has(c.id);
      $[58] = filteredIds;
      $[59] = t21;
    } else {
      t21 = $[59];
    }
    const filteredCasos = casos.filter(t21);
    const totalDaysOpen = filteredCasos.reduce((sum, c_0) => sum + differenceInDays(now, new Date(c_0.dataCriacao)), 0);
    avgDaysOpen = filteredCasos.length > 0 ? Math.round(totalDaysOpen / filteredCasos.length) : 0;
    agreementsClosed = filteredCasos.filter(_temp10).length;
    noAgreement = filteredCasos.filter(_temp11).length;
    const finalized = agreementsClosed + noAgreement;
    let t22;
    if ($[60] !== agreementsClosed || $[61] !== finalized) {
      t22 = finalized > 0 ? Math.round(agreementsClosed / finalized * 100) : 0;
      $[60] = agreementsClosed;
      $[61] = finalized;
      $[62] = t22;
    } else {
      t22 = $[62];
    }
    conversionRate = t22;
    totalValue = filteredCasos.filter(_temp12).reduce(_temp13, 0);
    casosComInteracao = filteredCasos.filter(_temp14);
    t19 = casosComInteracao.reduce(_temp15, 0);
    $[47] = casos;
    $[48] = filteredRows;
    $[49] = agreementsClosed;
    $[50] = avgDaysOpen;
    $[51] = casosComInteracao;
    $[52] = conversionRate;
    $[53] = noAgreement;
    $[54] = t19;
    $[55] = totalValue;
  } else {
    agreementsClosed = $[49];
    avgDaysOpen = $[50];
    casosComInteracao = $[51];
    conversionRate = $[52];
    noAgreement = $[53];
    t19 = $[54];
    totalValue = $[55];
  }
  const totalInteractionHours = t19;
  let t20;
  if ($[63] !== casosComInteracao.length || $[64] !== totalInteractionHours) {
    t20 = casosComInteracao.length > 0 ? Math.round(totalInteractionHours / casosComInteracao.length / 24) : 0;
    $[63] = casosComInteracao.length;
    $[64] = totalInteractionHours;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const avgResponseTime = t20;
  let t21;
  if ($[66] !== agreementsClosed || $[67] !== avgDaysOpen || $[68] !== avgResponseTime || $[69] !== conversionRate || $[70] !== metrics || $[71] !== noAgreement || $[72] !== totalValue) {
    t21 = {
      ...metrics,
      avgDaysOpen,
      conversionRate,
      totalValue,
      avgResponseTime,
      agreementsClosed,
      noAgreement
    };
    $[66] = agreementsClosed;
    $[67] = avgDaysOpen;
    $[68] = avgResponseTime;
    $[69] = conversionRate;
    $[70] = metrics;
    $[71] = noAgreement;
    $[72] = totalValue;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  const dashboardMetrics = t21;
  let t22;
  if ($[74] !== filteredRows) {
    t22 = new Set(filteredRows.map(_temp16));
    $[74] = filteredRows;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  const filteredIds_0 = t22;
  let last30Days;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let temperatureDistribution;
  if ($[76] !== casos || $[77] !== filteredIds_0 || $[78] !== metrics.cold || $[79] !== metrics.hot || $[80] !== metrics.warm) {
    let t28;
    if ($[88] !== filteredIds_0) {
      t28 = c_7 => filteredIds_0.has(c_7.id);
      $[88] = filteredIds_0;
      $[89] = t28;
    } else {
      t28 = $[89];
    }
    const filteredCasos_0 = casos.filter(t28);
    let t29;
    if ($[90] !== metrics.hot) {
      t29 = {
        name: "Quente",
        value: metrics.hot,
        color: CHART_COLORS.hot
      };
      $[90] = metrics.hot;
      $[91] = t29;
    } else {
      t29 = $[91];
    }
    let t30;
    if ($[92] !== metrics.warm) {
      t30 = {
        name: "Morna",
        value: metrics.warm,
        color: CHART_COLORS.warm
      };
      $[92] = metrics.warm;
      $[93] = t30;
    } else {
      t30 = $[93];
    }
    let t31;
    if ($[94] !== metrics.cold) {
      t31 = {
        name: "Fria",
        value: metrics.cold,
        color: CHART_COLORS.cold
      };
      $[94] = metrics.cold;
      $[95] = t31;
    } else {
      t31 = $[95];
    }
    let t32;
    if ($[96] !== t29 || $[97] !== t30 || $[98] !== t31) {
      t32 = [t29, t30, t31];
      $[96] = t29;
      $[97] = t30;
      $[98] = t31;
      $[99] = t32;
    } else {
      t32 = $[99];
    }
    temperatureDistribution = t32;
    const acordosMap = new Map();
    const semAcordoMap = new Map();
    for (const c_8 of filteredCasos_0) {
      if (c_8.status === "AGREEMENT_CLOSED") {
        const key = format(new Date(c_8.dataAtualizacao), "yyyy-MM-dd");
        acordosMap.set(key, (acordosMap.get(key) ?? 0) + 1);
      } else {
        if (c_8.status === "NO_AGREEMENT") {
          const key_0 = format(new Date(c_8.dataAtualizacao), "yyyy-MM-dd");
          semAcordoMap.set(key_0, (semAcordoMap.get(key_0) ?? 0) + 1);
        }
      }
    }
    const now_0 = new Date();
    last30Days = [];
    for (let i = 29; i >= 0; i--) {
      const date = subDays(now_0, i);
      const dateStr = format(date, "yyyy-MM-dd");
      const dayLabel = format(date, "dd/MM");
      last30Days.push({
        date: dayLabel,
        acordos: acordosMap.get(dateStr) ?? 0,
        semAcordo: semAcordoMap.get(dateStr) ?? 0
      });
    }
    const computeAvgDays = bucket => {
      const now_1 = new Date();
      const filtered = filteredCasos_0.filter(c_9 => c_9.cadenceBucket === bucket);
      if (filtered.length === 0) {
        return 0;
      }
      const total = filtered.reduce((sum_2, c_10) => sum_2 + differenceInDays(now_1, new Date(c_10.dataCriacao)), 0);
      return Math.round(total / filtered.length);
    };
    const computeAvgResponse = bucket_0 => {
      const filtered_0 = filteredCasos_0.filter(c_11 => c_11.cadenceBucket === bucket_0 && c_11.ultimaMensagemEm);
      if (filtered_0.length === 0) {
        return 0;
      }
      const total_0 = filtered_0.reduce(_temp17, 0);
      return Math.round(total_0 / filtered_0.length);
    };
    t26 = {
      temperature: "Quente",
      diasMedios: computeAvgDays("HOT"),
      tempoResposta: computeAvgResponse("HOT"),
      color: CHART_COLORS.hot
    };
    t27 = {
      temperature: "Morna",
      diasMedios: computeAvgDays("WARM"),
      tempoResposta: computeAvgResponse("WARM"),
      color: CHART_COLORS.warm
    };
    t23 = "Fria";
    t24 = computeAvgDays("COLD");
    t25 = computeAvgResponse("COLD");
    $[76] = casos;
    $[77] = filteredIds_0;
    $[78] = metrics.cold;
    $[79] = metrics.hot;
    $[80] = metrics.warm;
    $[81] = last30Days;
    $[82] = t23;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
    $[86] = t27;
    $[87] = temperatureDistribution;
  } else {
    last30Days = $[81];
    t23 = $[82];
    t24 = $[83];
    t25 = $[84];
    t26 = $[85];
    t27 = $[86];
    temperatureDistribution = $[87];
  }
  let t28;
  if ($[100] !== t23 || $[101] !== t24 || $[102] !== t25) {
    t28 = {
      temperature: t23,
      diasMedios: t24,
      tempoResposta: t25,
      color: CHART_COLORS.cold
    };
    $[100] = t23;
    $[101] = t24;
    $[102] = t25;
    $[103] = t28;
  } else {
    t28 = $[103];
  }
  let t29;
  if ($[104] !== t26 || $[105] !== t27 || $[106] !== t28) {
    t29 = [t26, t27, t28];
    $[104] = t26;
    $[105] = t27;
    $[106] = t28;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  const timeMetrics = t29;
  let t30;
  if ($[108] !== last30Days || $[109] !== temperatureDistribution || $[110] !== timeMetrics) {
    t30 = {
      temperatureDistribution,
      conversionTimeline: last30Days,
      timeMetrics
    };
    $[108] = last30Days;
    $[109] = temperatureDistribution;
    $[110] = timeMetrics;
    $[111] = t30;
  } else {
    t30 = $[111];
  }
  const chartData = t30;
  let t31;
  let t32;
  if ($[112] !== dispatch) {
    t31 = () => {
      dispatch(fetchNegociacaoCasos({
        page: 1,
        limit: FETCH_LIMIT
      }));
    };
    t32 = [dispatch];
    $[112] = dispatch;
    $[113] = t31;
    $[114] = t32;
  } else {
    t31 = $[113];
    t32 = $[114];
  }
  useEffect(t31, t32);
  let t33;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = value => {
      setFilters(prev => ({
        ...prev,
        search: value
      }));
    };
    $[115] = t33;
  } else {
    t33 = $[115];
  }
  const onSearchChange = t33;
  let t34;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = value_0 => {
      setFilters(prev_0 => {
        const current = prev_0.type;
        const next = current.includes(value_0) ? current.filter(v => v !== value_0) : [...current, value_0];
        return {
          ...prev_0,
          type: next
        };
      });
    };
    $[116] = t34;
  } else {
    t34 = $[116];
  }
  const onTypeToggle = t34;
  let t35;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = value_1 => {
      setFilters(prev_1 => {
        const current_0 = prev_1.status;
        const next_0 = current_0.includes(value_1) ? current_0.filter(v_0 => v_0 !== value_1) : [...current_0, value_1];
        return {
          ...prev_1,
          status: next_0
        };
      });
    };
    $[117] = t35;
  } else {
    t35 = $[117];
  }
  const onStatusToggle = t35;
  let t36;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => {
      setFilters(defaultFilters);
    };
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  const onClearFilters = t36;
  let t37;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = newFilters => {
      setFilters(newFilters);
    };
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  const applyFilters = t37;
  let t38;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = id => {
      setSelectedIds(prev_2 => {
        const next_1 = new Set(prev_2);
        if (next_1.has(id)) {
          next_1.delete(id);
        } else {
          next_1.add(id);
        }
        return next_1;
      });
    };
    $[120] = t38;
  } else {
    t38 = $[120];
  }
  const toggleSelect = t38;
  let t39;
  if ($[121] !== sections) {
    t39 = sectionId => {
      const section = sections.find(s_3 => s_3.id === sectionId);
      if (!section) {
        return;
      }
      const sectionItemIds = section.items.map(_temp18);
      setSelectedIds(prev_3 => {
        const next_2 = new Set(prev_3);
        const allSelected = sectionItemIds.every(id_0 => prev_3.has(id_0));
        if (allSelected) {
          sectionItemIds.forEach(id_1 => next_2.delete(id_1));
        } else {
          sectionItemIds.forEach(id_2 => next_2.add(id_2));
        }
        return next_2;
      });
    };
    $[121] = sections;
    $[122] = t39;
  } else {
    t39 = $[122];
  }
  const toggleSelectAll = t39;
  let t40;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => {
      setSelectedIds(new Set());
    };
    $[123] = t40;
  } else {
    t40 = $[123];
  }
  const clearSelection = t40;
  let t41;
  if ($[124] !== casos) {
    t41 = item_0 => {
      const rawCaso = casos.find(c_13 => c_13.id === item_0.id);
      const quickData = {
        id: item_0.id,
        client: item_0.client,
        negotiationName: item_0.negotiationName,
        status: item_0.status,
        statusValue: item_0.statusValue,
        statusVariant: item_0.statusVariant,
        temperature: item_0.temperature,
        type: item_0.type,
        objective: item_0.objective,
        objectiveValue: item_0.objectiveValue,
        createdAt: item_0.createdAt,
        totalMessages: rawCaso?.totalMensagens ?? 0,
        totalOffers: rawCaso?.totalOfertas ?? 0,
        lastOurOffer: rawCaso?.ultimaOfertaNossa ?? null,
        lastCounterpartyOffer: rawCaso?.ultimaOfertaContraparte ?? null,
        counterpartyPhone: rawCaso?.counterpartyPhone ?? "",
        counterpartyEmail: rawCaso?.counterpartyEmail ?? null,
        pendency: item_0.pendency,
        isPaused: item_0.isPaused
      };
      setQuickViewData(quickData);
      setIsQuickViewOpen(true);
    };
    $[124] = casos;
    $[125] = t41;
  } else {
    t41 = $[125];
  }
  const openQuickView = t41;
  let t42;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => {
      setIsQuickViewOpen(false);
      setTimeout(() => setQuickViewData(null), 300);
    };
    $[126] = t42;
  } else {
    t42 = $[126];
  }
  const closeQuickView = t42;
  let t43;
  if ($[127] !== dispatch) {
    t43 = async caseId => {
      const result_0 = await dispatch(pauseNegociacaoCaso({
        caseId
      }));
      if (pauseNegociacaoCaso.fulfilled.match(result_0)) {
        toast.success("Negocia\xE7\xE3o pausada");
        return;
      }
      const errorMessage = typeof result_0.payload === "string" ? result_0.payload : result_0.error?.message || "Erro ao pausar negocia\xE7\xE3o";
      toast.error(errorMessage);
    };
    $[127] = dispatch;
    $[128] = t43;
  } else {
    t43 = $[128];
  }
  const pauseCaso = t43;
  let t44;
  if ($[129] !== dispatch) {
    t44 = async caseId_0 => {
      const result_1 = await dispatch(resumeNegociacaoCaso({
        caseId: caseId_0
      }));
      if (resumeNegociacaoCaso.fulfilled.match(result_1)) {
        toast.success("Negocia\xE7\xE3o retomada");
        return;
      }
      const errorMessage_0 = typeof result_1.payload === "string" ? result_1.payload : result_1.error?.message || "Erro ao retomar negocia\xE7\xE3o";
      toast.error(errorMessage_0);
    };
    $[129] = dispatch;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  const resumeCaso = t44;
  let t45;
  if ($[131] !== dispatch) {
    t45 = async caseId_1 => {
      const result_2 = await dispatch(archiveNegociacaoCaso({
        caseId: caseId_1
      }));
      if (archiveNegociacaoCaso.fulfilled.match(result_2)) {
        toast.success("Negocia\xE7\xE3o arquivada");
        return;
      }
      const errorMessage_1 = typeof result_2.payload === "string" ? result_2.payload : result_2.error?.message || "Erro ao arquivar negocia\xE7\xE3o";
      toast.error(errorMessage_1);
    };
    $[131] = dispatch;
    $[132] = t45;
  } else {
    t45 = $[132];
  }
  const archiveCaso = t45;
  let t46;
  if ($[133] !== dispatch || $[134] !== filteredRows || $[135] !== selectedIds) {
    t46 = async () => {
      const selectedItems = filteredRows.filter(n_6 => selectedIds.has(n_6.id));
      const idsToPause = selectedItems.filter(_temp19).map(_temp20);
      if (idsToPause.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser pausada");
        return {
          successCount: 0,
          failCount: 0,
          totalAttempted: 0
        };
      }
      const results = await batchDispatch(idsToPause, id_3 => dispatch(pauseNegociacaoCaso({
        caseId: id_3
      })));
      const successCount = results.filter(_temp21).length;
      const failCount = results.length - successCount;
      if (successCount > 0) {
        toast.success(`${successCount} negociações pausadas`);
        clearSelection();
      }
      if (failCount > 0) {
        toast.error(`${failCount} negociações não puderam ser pausadas`);
      }
      return {
        successCount,
        failCount,
        totalAttempted: idsToPause.length
      };
    };
    $[133] = dispatch;
    $[134] = filteredRows;
    $[135] = selectedIds;
    $[136] = t46;
  } else {
    t46 = $[136];
  }
  const pauseSelected = t46;
  let t47;
  if ($[137] !== dispatch || $[138] !== filteredRows || $[139] !== selectedIds) {
    t47 = async () => {
      const selectedItems_0 = filteredRows.filter(n_9 => selectedIds.has(n_9.id));
      const idsToResume = selectedItems_0.filter(_temp22).map(_temp23);
      if (idsToResume.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser retomada");
        return {
          successCount: 0,
          failCount: 0,
          totalAttempted: 0
        };
      }
      const results_0 = await batchDispatch(idsToResume, id_4 => dispatch(resumeNegociacaoCaso({
        caseId: id_4
      })));
      const successCount_0 = results_0.filter(_temp24).length;
      const failCount_0 = results_0.length - successCount_0;
      if (successCount_0 > 0) {
        toast.success(`${successCount_0} negociações retomadas`);
        clearSelection();
      }
      if (failCount_0 > 0) {
        toast.error(`${failCount_0} negociações não puderam ser retomadas`);
      }
      return {
        successCount: successCount_0,
        failCount: failCount_0,
        totalAttempted: idsToResume.length
      };
    };
    $[137] = dispatch;
    $[138] = filteredRows;
    $[139] = selectedIds;
    $[140] = t47;
  } else {
    t47 = $[140];
  }
  const resumeSelected = t47;
  let t48;
  if ($[141] !== dispatch || $[142] !== filteredRows || $[143] !== selectedIds) {
    t48 = async () => {
      const idsToArchive = filteredRows.filter(n_12 => selectedIds.has(n_12.id)).map(_temp25);
      if (idsToArchive.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada");
        return {
          successCount: 0,
          failCount: 0,
          totalAttempted: 0
        };
      }
      const results_1 = await batchDispatch(idsToArchive, id_5 => dispatch(archiveNegociacaoCaso({
        caseId: id_5
      })));
      const successCount_1 = results_1.filter(_temp26).length;
      const failCount_1 = results_1.length - successCount_1;
      if (successCount_1 > 0) {
        toast.success(`${successCount_1} negociações arquivadas`);
        clearSelection();
      }
      if (failCount_1 > 0) {
        toast.error(`${failCount_1} negociações não puderam ser arquivadas`);
      }
      return {
        successCount: successCount_1,
        failCount: failCount_1,
        totalAttempted: idsToArchive.length
      };
    };
    $[141] = dispatch;
    $[142] = filteredRows;
    $[143] = selectedIds;
    $[144] = t48;
  } else {
    t48 = $[144];
  }
  const archiveSelected = t48;
  let t49;
  if ($[145] !== dispatch || $[146] !== selectedIds) {
    t49 = async format$0 => {
      if (selectedIds.size === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada");
        return;
      }
      const result_3 = await dispatch(exportNegociacaoCasosCsv({
        caseIds: Array.from(selectedIds),
        format: format$0
      }));
      if (exportNegociacaoCasosCsv.fulfilled.match(result_3)) {
        toast.success(`${format$0 === "xlsx" ? "Excel" : "CSV"} exportado com sucesso`);
        return;
      }
      const errorMessage_2 = typeof result_3.payload === "string" ? result_3.payload : result_3.error?.message || "Erro ao exportar";
      toast.error(errorMessage_2);
    };
    $[145] = dispatch;
    $[146] = selectedIds;
    $[147] = t49;
  } else {
    t49 = $[147];
  }
  const exportSelected = t49;
  let t50;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = sectionId_0 => {
      setExpandedSections(prev_4 => {
        const next_3 = new Set(prev_4);
        if (next_3.has(sectionId_0)) {
          next_3.delete(sectionId_0);
        } else {
          next_3.add(sectionId_0);
        }
        return next_3;
      });
    };
    $[148] = t50;
  } else {
    t50 = $[148];
  }
  const toggleSectionExpand = t50;
  const scrollToSection = _temp27;
  let t51;
  if ($[149] !== dispatch) {
    t51 = () => {
      dispatch(fetchNegociacaoCasos({
        page: 1,
        limit: FETCH_LIMIT
      }));
    };
    $[149] = dispatch;
    $[150] = t51;
  } else {
    t51 = $[150];
  }
  const refetch = t51;
  const t52 = error ?? null;
  let t53;
  if ($[151] !== archiveCaso || $[152] !== archiveSelected || $[153] !== chartData || $[154] !== dashboardMetrics || $[155] !== expandedSections || $[156] !== exportSelected || $[157] !== filters || $[158] !== isExportingCsv || $[159] !== isLoading || $[160] !== isQuickViewOpen || $[161] !== metrics || $[162] !== openQuickView || $[163] !== pauseCaso || $[164] !== pauseSelected || $[165] !== quickViewData || $[166] !== refetch || $[167] !== resumeCaso || $[168] !== resumeSelected || $[169] !== sections || $[170] !== selectedIds || $[171] !== t52 || $[172] !== toggleSelectAll) {
    t53 = {
      sections,
      metrics,
      dashboardMetrics,
      chartData,
      filters,
      selectedIds,
      isLoading,
      error: t52,
      quickViewData,
      isQuickViewOpen,
      expandedSections,
      isExportingCsv,
      onSearchChange,
      onTypeToggle,
      onStatusToggle,
      onClearFilters,
      applyFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      openQuickView,
      closeQuickView,
      pauseCaso,
      resumeCaso,
      archiveCaso,
      pauseSelected,
      resumeSelected,
      archiveSelected,
      exportSelected,
      toggleSectionExpand,
      scrollToSection,
      refetch
    };
    $[151] = archiveCaso;
    $[152] = archiveSelected;
    $[153] = chartData;
    $[154] = dashboardMetrics;
    $[155] = expandedSections;
    $[156] = exportSelected;
    $[157] = filters;
    $[158] = isExportingCsv;
    $[159] = isLoading;
    $[160] = isQuickViewOpen;
    $[161] = metrics;
    $[162] = openQuickView;
    $[163] = pauseCaso;
    $[164] = pauseSelected;
    $[165] = quickViewData;
    $[166] = refetch;
    $[167] = resumeCaso;
    $[168] = resumeSelected;
    $[169] = sections;
    $[170] = selectedIds;
    $[171] = t52;
    $[172] = toggleSelectAll;
    $[173] = t53;
  } else {
    t53 = $[173];
  }
  return t53;
};
function _temp(n_2) {
  return n_2.needsAction;
}
function _temp2(n_3) {
  return n_3.temperature === "Quente" && !n_3.needsAction;
}
function _temp3(n_4) {
  return n_4.temperature === "Morna" && !n_4.needsAction;
}
function _temp4(n_5) {
  return n_5.temperature === "Fria" && !n_5.needsAction;
}
function _temp5(s) {
  return s.id === "INTERVENTION";
}
function _temp6(s_0) {
  return s_0.id === "HOT";
}
function _temp7(s_1) {
  return s_1.id === "WARM";
}
function _temp8(s_2) {
  return s_2.id === "COLD";
}
function _temp9(r) {
  return r.id;
}
function _temp10(c_1) {
  return c_1.status === "AGREEMENT_CLOSED";
}
function _temp11(c_2) {
  return c_2.status === "NO_AGREEMENT";
}
function _temp12(c_3) {
  return c_3.status === "ACTIVE" || c_3.status === "SCHEDULED" || c_3.status === "PAUSED";
}
function _temp13(sum_0, c_4) {
  return sum_0 + (c_4.claimAmount || 0);
}
function _temp14(c_5) {
  return c_5.ultimaMensagemEm && c_5.dataCriacao;
}
function _temp15(sum_1, c_6) {
  const created = new Date(c_6.dataCriacao);
  const lastMsg = new Date(c_6.ultimaMensagemEm);
  return sum_1 + differenceInHours(lastMsg, created);
}
function _temp16(r_0) {
  return r_0.id;
}
function _temp17(sum_3, c_12) {
  return sum_3 + differenceInHours(new Date(c_12.ultimaMensagemEm), new Date(c_12.dataCriacao)) / 24;
}
function _temp18(item) {
  return item.id;
}
function _temp19(n_7) {
  return n_7.statusValue !== "PAUSED" && n_7.statusValue !== "AGREEMENT_CLOSED" && n_7.statusValue !== "NO_AGREEMENT" && !n_7.isArchived;
}
function _temp20(n_8) {
  return n_8.id;
}
function _temp21(r_1) {
  return pauseNegociacaoCaso.fulfilled.match(r_1);
}
function _temp22(n_10) {
  return n_10.statusValue === "PAUSED";
}
function _temp23(n_11) {
  return n_11.id;
}
function _temp24(r_2) {
  return resumeNegociacaoCaso.fulfilled.match(r_2);
}
function _temp25(n_13) {
  return n_13.id;
}
function _temp26(r_3) {
  return archiveNegociacaoCaso.fulfilled.match(r_3);
}
function _temp27(sectionId_1) {
  const element = document.getElementById(`section-${sectionId_1}`);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }
}